/**
 * Application Routes Constants
 *
 * Centralized location for all route definitions to make updates easier.
 * All routes should be defined here and imported throughout the application.
 */

import { publicCategorySlug } from "@/lib/category-slugs"
import { regionPrefix, splitRegion } from "@/lib/region"
import { queryToUrlParams } from "@/lib/services/search/search"
import type { PlanChoice } from "@/lib/types/checkout"
import { DEFAULT_SEARCH_QUERY } from "@/lib/types/search"
import type { SearchQuery } from "@/lib/types/search"
import { joinPrefix } from "@/lib/url-prefix"

import type { Route } from "next"

/**
 * Generic route builder for routes that use handles
 * @param baseRoute - Base route from ROUTES constant
 * @param handle - Handle to append to the route
 * @returns Complete route string
 */
const buildHandleRoute = (baseRoute: string, handle?: string | null) => `/${baseRoute}/${handle || ""}`

// Base routes
export const ROUTES = {
  // Home — the region root (/ for the default region, /<cc> otherwise); the feed home serves
  // there.
  HOME: "",

  // Spanish routes (current)
  CART: "carrito",
  WISHLIST: "favoritos",
  CATEGORIES: "categorias",
  COLLECTIONS: "colecciones",
  PRODUCTS: "productos",
  SEARCH: "busqueda",
  ACCOUNT: "cuenta",
  REFERRALS: "referidos",
  SIGN_IN: "iniciar-sesion",
  SIGN_UP: "registro",
  CHECKOUT: "compra",
  MERCHANT_CHECKOUT: "checkout",

  // Other routes
  HOW_TO_BUY: "como-comprar",
  FOR_MERCHANTS: "para-comercios",
  VERIFY: "verificar",
} as const

export const RETURN_PARAM = "volver"
export const VERIFY_CART_PARAM = "carrito"
export const VERIFY_REENGAGEMENT_PARAM = "reenganche"

export const isVerifyReengagement = (searchParams: Pick<URLSearchParams, "get">): boolean =>
  searchParams.get(VERIFY_REENGAGEMENT_PARAM) === "1" || searchParams.get(VERIFY_CART_PARAM) === null
export const INSTALLMENTS_PARAM = "cuotas"
export const BNPL_OPTION_PARAM = "opcion"
export const BNPL_NEW_USER_PARAM = "nuevo"
const MAX_INSTALLMENTS = 48

export const SECURED_PLAN_PARAM = "garantia"

export const parsePlan = (installments: string | null | undefined, secured: string | null | undefined): PlanChoice | undefined => {
  const parsed = Number(installments)
  if (!Number.isInteger(parsed) || parsed <= 0 || parsed > MAX_INSTALLMENTS) return undefined
  return { installments: parsed, financingMode: secured === "1" ? "SECURED" : "REGULAR" }
}

const setPlan = (params: URLSearchParams, plan?: PlanChoice): void => {
  if (!plan) return
  params.set(INSTALLMENTS_PARAM, String(plan.installments))
  if (plan.financingMode === "SECURED") params.set(SECURED_PLAN_PARAM, "1")
}

const withPlan = (path: string, plan?: PlanChoice): string => {
  const params = new URLSearchParams()
  setPlan(params, plan)
  const query = params.toString()
  return query ? `${path}${path.includes("?") ? "&" : "?"}${query}` : path
}

export const SIGN_IN_STEPS = ["codigo", "nip", "crear-nip", "recuperar-nip"] as const
export const SIGN_UP_STEPS = ["codigo", "datos-personales", "curp", "ocupacion-ingresos", "direccion"] as const
export const CHECKOUT_STEPS = ["espera", "direccion", "pago", "exito", "rechazada"] as const

export type SignInStep = (typeof SIGN_IN_STEPS)[number]
export type SignUpStep = (typeof SIGN_UP_STEPS)[number]
export type VerifyStep = "documento" | "captura" | "estado" | "ayuda"
export type CheckoutStep = (typeof CHECKOUT_STEPS)[number]
export type AccountPage = "datos" | "direcciones" | "pedidos"

export const isCheckoutStep = (value: string | null | undefined): value is CheckoutStep => CHECKOUT_STEPS.some((step) => step === value)

export const CART_LIST_PARAM = "lista"
export const CART_LIST_SAVED = "favoritos"

const OFFERS_HANDLE = "descuentos"
const BEST_SELLERS_HANDLE = "los-mas-vendidos"

const withReturn = (path: string, returnTo?: string) => (returnTo ? `${path}?${RETURN_PARAM}=${encodeURIComponent(returnTo)}` : path)

export const segmentsAfterRegion = (pathname: string): string[] => splitRegion(pathname).rest.split("/").filter(Boolean)

// Route builders for dynamic routes
export const buildRoute = {
  /**
   * Build product route with handle
   * @param handle - Product handle
   * @returns Product route string
   */
  product: (handle?: string | null) => buildHandleRoute(ROUTES.PRODUCTS, handle),

  /**
   * Build product route from full product URL
   * Extracts the handle from API URLs like "https://tienda.nelo.mx/mx/productos/laptop-handle"
   * @param productUrl - Full product URL from API
   * @returns Product route string
   */
  productFromUrl: (productUrl: string | null | undefined) => {
    if (!productUrl) return buildHandleRoute(ROUTES.PRODUCTS, null)
    const segments = productUrl.split("/").filter(Boolean)
    const handle = segments[segments.length - 1] || ""
    return buildHandleRoute(ROUTES.PRODUCTS, handle)
  },

  /**
   * Build category route with category path
   * @param categoryPath - Array of category segments or single category
   * @returns Category route string
   */
  category: (categoryPath?: string | (string | null | undefined)[]) => {
    if (!categoryPath) return `/${ROUTES.CATEGORIES}`
    const segments = Array.isArray(categoryPath) ? categoryPath.filter((val): val is string => val !== null && val !== undefined) : [categoryPath]
    const last = segments.at(-1)
    return buildHandleRoute(ROUTES.CATEGORIES, last ? publicCategorySlug(last) : "")
  },

  /**
   * Build collection route with handle
   * @param handle - Collection handle
   * @returns Collection route string
   */
  collection: (handle?: string | null) => buildHandleRoute(ROUTES.COLLECTIONS, handle),

  offers: () => buildRoute.collection(OFFERS_HANDLE),

  bestSellers: () => buildRoute.collection(BEST_SELLERS_HANDLE),

  account: () => `/${ROUTES.ACCOUNT}`,

  referrals: () => `/${ROUTES.ACCOUNT}/${ROUTES.REFERRALS}`,

  saved: () => `/${ROUTES.WISHLIST}`,

  accountAddresses: () => `/${ROUTES.ACCOUNT}/direcciones`,

  accountPage: (page: AccountPage) => `/${ROUTES.ACCOUNT}/${page}`,

  accountOrder: (orderId: string) => `/${ROUTES.ACCOUNT}/pedidos/${orderId}`,

  accountOrderTracking: (orderId: string) => `/${ROUTES.ACCOUNT}/pedidos/${orderId}/seguimiento`,

  signIn: (step?: SignInStep, returnTo?: string) => withReturn(step ? `/${ROUTES.SIGN_IN}/${step}` : `/${ROUTES.SIGN_IN}`, returnTo),

  signUp: (step?: SignUpStep, returnTo?: string) => withReturn(step ? `/${ROUTES.SIGN_UP}/${step}` : `/${ROUTES.SIGN_UP}`, returnTo),

  verify: (step?: VerifyStep, cartId?: string, plan?: PlanChoice) => {
    const path = step ? `/${ROUTES.VERIFY}/${step}` : `/${ROUTES.VERIFY}`
    return withPlan(cartId ? `${path}?${VERIFY_CART_PARAM}=${encodeURIComponent(cartId)}` : path, plan)
  },

  checkout: (item?: { productId: string; variantId: string; previewHandle?: string; plan?: PlanChoice; source?: string }) => {
    if (!item) return `/${ROUTES.CHECKOUT}`
    const params = new URLSearchParams({ productId: item.productId, variantId: item.variantId })
    if (item.previewHandle) params.set("preview", item.previewHandle)
    setPlan(params, item.plan)
    if (item.source) params.set("source", item.source)
    return `/${ROUTES.CHECKOUT}?${params}`
  },

  checkoutStep: (cartId: string, step: CheckoutStep, plan?: PlanChoice) => withPlan(`/${ROUTES.CHECKOUT}/${cartId}/${step}`, plan),

  merchantCheckout: (token: string) => `/${ROUTES.MERCHANT_CHECKOUT}/${token}`,

  /**
   * Build search route with query and filters
   * @param searchQuery - Partial SearchQuery object with filters
   * @returns Search route string
   */
  search: (searchQuery: Partial<SearchQuery>, baseQuery: SearchQuery = DEFAULT_SEARCH_QUERY) => {
    const query: SearchQuery = {
      ...baseQuery,
      ...searchQuery,
    }

    const params = queryToUrlParams(query, baseQuery)
    const queryString = params.toString()
    return queryString ? `/${ROUTES.SEARCH}?${queryString}` : `/${ROUTES.SEARCH}`
  },

  /**
   * Build external signup URL with tracking params
   * @param sessionId - Storefront session ID
   * @returns Complete external signup URL with query params
   */
  externalSignup: (sessionId: string, trackingParams: Record<string, string> = {}) => {
    const params = new URLSearchParams({
      source: "marketplace",
      storefrontSessionId: sessionId,
      ...trackingParams,
    })
    return `${process.env.NEXT_PUBLIC_SIGNUP_ORIGIN}/es/registrate/a/email-telefono?${params}`
  },
}

export const asRoute = (href: string): Route => href as Route

/**
 * Place a route path under its region (the programmatic counterpart to LocalizedClientLink).
 * The default region is served at the root, so its paths come back unchanged; any other
 * region gets its /<cc> prefix. Use for router.push and redirects so components never
 * hand-concatenate region paths.
 * @param path - A path from buildRoute (region-less, e.g. "/busqueda?q=…")
 * @param countryCode - The active region code
 */
export const localizeHref = (path: string, countryCode: string): Route => asRoute(joinPrefix(regionPrefix(countryCode), path))

export const pathWithQuery = (pathname: string, queryString: string): Route => asRoute(queryString ? `${pathname}?${queryString}` : pathname)

export const SESSION_EXPIRED_PARAM = "expired"

// Route validation helpers
export const isRoute = {
  /**
   * Check if pathname is a category page
   * @param pathname - Current pathname
   * @returns True if on category page
   */
  category: (pathname: string) => pathname.includes(ROUTES.CATEGORIES),

  /**
   * Check if pathname is a collection page
   * @param pathname - Current pathname
   * @returns True if on collection page
   */
  collection: (pathname: string) => pathname.includes(ROUTES.COLLECTIONS),

  /**
   * Check if pathname is a product page
   * @param pathname - Current pathname
   * @returns True if on product page
   */
  product: (pathname: string) => pathname.includes(`/${ROUTES.PRODUCTS}/`),

  /**
   * Check if pathname is a category or collection page
   * @param pathname - Current pathname
   * @returns True if on category or collection page
   */
  categoryOrCollection: (pathname: string) => isRoute.category(pathname) || isRoute.collection(pathname),

  /**
   * Check if pathname is a cart page
   * @param pathname - Current pathname
   * @returns True if on cart page
   */
  cart: (pathname: string) => pathname.includes(ROUTES.CART),

  /**
   * Check if pathname is a search page
   * @param pathname - Current pathname
   * @returns True if on search page
   */
  search: (pathname: string) => pathname.includes(ROUTES.SEARCH),
}

/** The request's query string, `?`-prefixed or empty, with `drop` removed — for redirects that keep the shopper's filters. */
export const searchSuffix = (params: Record<string, string | string[] | undefined>, drop?: string): string => {
  const kept = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (key === drop || value === undefined) continue
    for (const entry of Array.isArray(value) ? value : [value]) kept.append(key, entry)
  }
  const search = kept.toString()
  return search ? `?${search}` : ""
}
